/**
 * Pure utility functions for the asset assignment form state.
 *
 * These functions encapsulate the form logic for the AssignAssetDialog,
 * enabling property-based testing of correctness invariants.
 */

export interface AssignmentFormState {
  customerId: string | null
  siteId: string | null
  systemId: string | null
  newSystemName?: string | null
}

/**
 * Determines whether the assignment form is valid for submission.
 *
 * PRECONDITION: state is the current form state
 * POSTCONDITION: returns true iff mandatory fields are filled
 * Note: systemId is optional and does NOT affect validity
 */
export function isFormValid(state: AssignmentFormState): boolean {
  return state.customerId !== null && state.siteId !== null
}

/**
 * Determines whether the site select field should be disabled.
 * Site is disabled when no customer is selected.
 */
export function isSiteDisabled(state: AssignmentFormState): boolean {
  return state.customerId === null
}

/**
 * Determines whether the system select field should be disabled.
 * System is disabled when no site is selected.
 */
export function isSystemDisabled(state: AssignmentFormState): boolean {
  return state.siteId === null
}

/**
 * Handles customer change: resets site and system selections.
 */
export function handleCustomerChange(newCustomerId: string | null): AssignmentFormState {
  return {
    customerId: newCustomerId,
    siteId: null,
    systemId: null,
  }
}

/**
 * Handles site change: resets system selection, preserves customer.
 */
export function handleSiteChange(
  currentState: AssignmentFormState,
  newSiteId: string | null,
): AssignmentFormState {
  return {
    ...currentState,
    siteId: newSiteId,
    systemId: null,
  }
}

/**
 * Handles system change: preserves customer and site.
 */
export function handleSystemChange(
  currentState: AssignmentFormState,
  newSystemId: string | null,
): AssignmentFormState {
  return {
    ...currentState,
    systemId: newSystemId,
  }
}
